import { useState } from 'react'

import { Box, Typography } from '@mui/material'

import { useClampOverflow } from 'hooks'

interface ClampedRichTextProps {
  /** Rich-text HTML, as stored by `RichTextEditorField`. */
  html: string
  /** Lines shown before clamping. */
  lines?: number
}

/**
 * Rich text clamped to `lines`, with a "Show more" toggle that appears ONLY when
 * the content actually overflows.
 *
 * ## Why a measured clamp rather than an accordion
 *
 * This replaced an `<Accordion defaultExpanded>` on the product page. Spencer
 * asked whether the description could still be an accordion; the answer was a
 * clamp, because an always-present chevron is chrome no other field in that card
 * has — it announces "there is more here" even when the value is one line, which
 * most product descriptions are. The accordion also never persisted its collapsed
 * state, so it rendered open on every load regardless.
 *
 * A clamp inverts that: short content gets NO control at all, and the control
 * appears exactly when it earns its place. The measurement is what makes that
 * possible and is the part worth being careful about — see `useClampOverflow`,
 * which this shares with `AnnouncementCard` rather than re-deriving (§36).
 *
 * ⚠️ `dangerouslySetInnerHTML` is carried over from the accordion, not introduced
 * here. The `products.description` column has always stored markup written
 * through the edit page's `RichTextEditorField`, and this is the only thing that
 * renders it. If this component is ever pointed at a field whose HTML is not
 * authored through that editor, sanitise first.
 */
export const ClampedRichText = ({ html, lines = 3 }: ClampedRichTextProps) => {
  const [expanded, setExpanded] = useState(false)
  /*
    Measurement runs only while COLLAPSED. Expanded, the element has no clamp, so
    `scrollHeight === clientHeight` and the hook correctly reports `overflows:
    false`.

    Which is why the toggle is rendered on `expanded || overflows`, NOT on
    `overflows` alone. On `overflows` alone the control pulls itself out from
    under the reader the instant they use it: they click "Show more", the clamp
    comes off, the measurement goes false, and the "Show less" that should have
    replaced it is gone — text expanded with no way back. Caught in the browser,
    not in review; an earlier revision of this file carried a comment claiming to
    prevent exactly this while doing the opposite.

    `expanded` is sound as a standalone reason to show the control: the only way
    to reach that state is to have clicked the control, which only exists when
    the content overflowed.
  */
  const { ref, overflows } = useClampOverflow(!expanded, html)
  const showToggle = expanded || overflows

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        ref={ref}
        sx={{
          lineHeight: 1.6,
          color: 'text.primary',
          // Zero the first child's top margin and the last child's bottom margin:
          // a leading <p> would otherwise double the row's own padding.
          '& :first-of-type': { mt: 0 },
          '& :last-child': { mb: 0 },
          ...(expanded
            ? null
            : {
                /*
                  `-webkit-line-clamp` is the only cross-browser way to clamp at a
                  LINE count rather than a pixel height, and despite the prefix it
                  is supported everywhere this app runs (it is specified in
                  CSS Overflow 3). A `maxHeight` in px would be wrong the moment a
                  description mixes font sizes or contains a list.

                  All four properties are required together — the clamp is inert
                  without `display: -webkit-box` and `overflow: hidden`.
                */
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: lines,
                overflow: 'hidden',
              }),
        }}
        dangerouslySetInnerHTML={{ __html: html }}
      />
      {showToggle && (
        /*
          A button, not a styled <span>: it is a real control and must be
          reachable by keyboard and announced as a button. `aria-expanded` is what
          tells a screen-reader user which state they are in — without it the
          label alone flips between two words with no indication that it toggles
          the text above.
        */
        <Typography
          component="button"
          type="button"
          variant="body3"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          sx={{
            mt: 2,
            p: 0,
            border: 0,
            background: 'none',
            cursor: 'pointer',
            fontWeight: 500,
            color: 'text.secondary',
            textDecoration: 'underline',
            textUnderlineOffset: 2,
            '&:hover': { color: 'text.primary' },
          }}
        >
          {expanded ? 'Show less' : 'Show more'}
        </Typography>
      )}
    </Box>
  )
}
